<template>
  <ProductListDetailArea>
    <ProductlistDetailModalArea>
      <!-- ! 헤더 -->
      <ProdcutDetailModalHeader>
        <HeaderTopSection>
          <!-- * 품목명 -->
          <ProductListDetailModalHeaderTitle class="text-truncate" :title="Item.name3">
            {{ Item.name3 }}
          </ProductListDetailModalHeaderTitle>

          <!-- * QR 코드 섹션 -->
          <div class="qr-code-section">
            <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="QR Code" />
            <ProductListDetailBtn @click="downloadQRCode" :disabled="!qrCodeUrl">
              QR 코드<br />다운로드
            </ProductListDetailBtn>
          </div>
        </HeaderTopSection>

        <BarcodeHashtagContainer>
          <!-- * 바코드 스캔 -->
          <ProdcutlistDetailBarcodeScanArea>
            <p>바코드 스캔</p>
            <BarcodeDisplayArea :class="{ scanning: isScannerActive }">
              <div class="barcode-status-container">
                <template v-if="!scannedBarcode">
                  <div class="barcode-empty" :class="{ 'scanning-prompt': isScannerActive }">
                    <template v-if="isScannerActive">
                      <BarcodeScan :size="25" />
                      <span>바코드를 스캔해주세요</span>
                    </template>
                    <template v-else>
                      <XIcon :size="25" />
                      <span>바코드없음</span>
                    </template>
                  </div>
                </template>
                <template v-else>
                  <div class="barcode-value">
                    <BarcodeIcon :size="35" />
                    <div class="barcode-number" :title="scannedBarcode">
                      {{ scannedBarcode }}
                    </div>
                  </div>
                </template>
              </div>
              <ScanButton v-if="!scannedBarcode" @click="toggleBarcodeScanner" :class="{ active: isScannerActive }">
                <span>{{ isScannerActive ? "취소" : "바코드 스캔" }}</span>
              </ScanButton>
              <DeleteBarcodeBtn v-else @click="deleteBarcode">삭제</DeleteBarcodeBtn>
            </BarcodeDisplayArea>
          </ProdcutlistDetailBarcodeScanArea>
          <!-- * 해시태그 영역 -->
          <HashtagArea>
            <p>해시태그</p>
            <HashtagContent>
              <template v-if="isEditMode">
                <div class="hashtag-input-container">
                  <ProductListDetailInput v-model="newHashtagInput" @focus="disableBarcodeScanner"
                    @blur="enableBarcodeScanner" @keyup.enter="addHashtag" type="text"
                    placeholder="해시태그 입력 후 엔터를 눌러주세요." />
                </div>
              </template>
              <div class="hashtag-list">
                <template v-if="displayHashtags && displayHashtags.length > 0">
                  <span v-for="(tag, index) in displayHashtags" :key="index" class="hashtag-item">
                    #{{ tag }}
                    <button v-if="isEditMode" class="hashtag-delete" @click.prevent="deleteHashtag(index)"
                      type="button">
                      ×
                    </button>
                  </span>
                </template>
                <div v-else class="no-tags">등록된 해시태그가 없습니다.</div>
              </div>
            </HashtagContent>
          </HashtagArea>
        </BarcodeHashtagContainer>
      </ProdcutDetailModalHeader>

      <!-- ! 컨텐츠 -->
      <ProdcutDetailModalContent>
        <!-- * 이미지 -->
        <ProductImageSection>
          <ProductlistDetailImg :src="getImageUrl(Item)" :alt="Item.name3" @error="handleImageError" />
          <template v-if="isEditMode">
            <ImageInputContainer>
              <input id="imageInput" type="file" ref="imageInput" @change="handleImageSelect" accept="image/*" />
            </ImageInputContainer>
            <ProductListDetailImageDeleteBtn v-if="getImageUrl(Item) !== '/icon.png'" @click="deleteImage">
              X
            </ProductListDetailImageDeleteBtn>
          </template>
        </ProductImageSection>

        <ProductInfoSection>
          <InfoCard>
            <InfoLabel>카테고리1</InfoLabel>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.name0 }}
              </template>
              <template v-else>
                <ProductListDetailSelect v-model="editedItem.name0" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner">
                  <option value="">카테고리1 선택</option>
                  <option v-for="category in allCategories" :key="category.id" :value="category.name">
                    {{ category.name }}
                  </option>
                </ProductListDetailSelect>
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabel>카테고리2</InfoLabel>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.name1 }}
              </template>
              <template v-else>
                <ProductListDetailSelect v-model="editedItem.name1" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner" :disabled="!editedItem.name0">
                  <option value="">카테고리2 선택</option>
                  <option v-for="subCategory in filteredSubCategories" :key="subCategory.id" :value="subCategory.name">
                    {{ subCategory.name }}
                  </option>
                </ProductListDetailSelect>
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabelContainer>
              <InfoLabel>품목명</InfoLabel>
              <SearchIconButton v-if="isEditMode" @click="searchProductImages" :disabled="!editedItem.name3.trim()">
                <Camera />
                <IconTooltip>네이버 이미지 검색</IconTooltip>
              </SearchIconButton>
            </InfoLabelContainer>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.name3 }}
              </template>
              <template v-else>
                <ProductListDetailInput v-model="editedItem.name3" type="text" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner" @keydown.enter="searchProductImages" />
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabel>모델명</InfoLabel>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.name4 }}
              </template>
              <template v-else>
                <ProductListDetailInput v-model="editedItem.name4" type="text" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner" />
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabel>등록코드</InfoLabel>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.code }}
              </template>
              <template v-else>
                <ProductListDetailInput v-model="editedItem.code" type="text" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner" />
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabel>대상</InfoLabel>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.target }}
              </template>
              <template v-else>
                <ProductListDetailSelect v-model="editedItem.target" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner">
                  <option value="">선택하세요</option>
                  <option v-for="target in targetOptions" :key="target" :value="target">
                    {{ target }}
                  </option>
                </ProductListDetailSelect>
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabel>사이즈</InfoLabel>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.size }}
              </template>
              <template v-else>
                <ProductListDetailSelect v-model="editedItem.size" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner">
                  <option value="">선택하세요</option>
                  <option v-for="size in sizeOptions" :key="size" :value="size">
                    {{ size }}
                  </option>
                </ProductListDetailSelect>
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabel>색상</InfoLabel>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.color }}
              </template>
              <template v-else>
                <ProductListDetailSelect v-model="editedItem.color" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner">
                  <option value="">선택하세요</option>
                  <option v-for="color in colorOptions" :key="color" :value="color">
                    {{ color }}
                  </option>
                </ProductListDetailSelect>
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabel>바코드</InfoLabel>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.options.bcode[0] }}
              </template>
              <template v-else>
                <ProductListDetailInput v-model="editedItem.bcode" type="text" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner" />
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabel>과세구분</InfoLabel>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.tax }}
              </template>
              <template v-else>
                <ProductListDetailSelect v-model="editedItem.tax" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner">
                  <option value="">선택하세요</option>
                  <option v-for="tax in taxOptions" :key="tax" :value="tax">
                    {{ tax }}
                  </option>
                </ProductListDetailSelect>
              </template>
            </InfoValue>
          </InfoCard>

          <template v-if="Item.name0 === '당뇨용품'">
            <InfoCard>
              <InfoLabel>보험코드</InfoLabel>
              <InfoValue>
                <template v-if="!isEditMode">
                  {{ Item.icode }}
                </template>
                <template v-else>
                  <ProductListDetailInput v-model="editedItem.icode" type="text" @focus="disableBarcodeScanner"
                    @blur="enableBarcodeScanner" />
                </template>
              </InfoValue>
            </InfoCard>
          </template>

          <InfoCard class="location-card">
            <InfoLabel>재고 위치</InfoLabel>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.location }}
              </template>
              <template v-else>
                <ProductListDetailInput v-model="editedItem.location1" type="text" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner" />-
                <ProductListDetailInput v-model="editedItem.location2" type="text" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner" />
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabel>비고</InfoLabel>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.note }}
              </template>
              <template v-else>
                <ProductListDetailInput v-model="editedItem.note" type="text" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner" />
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard class="full-width">
            <InfoLabel>판매가격</InfoLabel>
            <InfoValue class="price_area">
              <template v-if="!isEditing">
                <div class="price-display">
                  <span class="price-text">{{ formattedPrice }}원</span>
                  <ProductListDetailBtn @click="toggleEditMode" class="edit-btn">
                    가격 수정
                  </ProductListDetailBtn>
                </div>
              </template>
              <template v-else>
                <div class="price-edit-container">
                  <ProductListDetailInput :value="editedPrice.replace(/,/g, '')" @input="onPriceInput"
                    @focus="disableBarcodeScanner" @blur="enableBarcodeScanner" type="text" @keyup.enter="updateProduct"
                    class="price-input" />
                  <ProductListDetailBtn @click="updateProduct" class="save-btn">
                    저장
                  </ProductListDetailBtn>
                  <ProductListDetailBtn @click="toggleEditMode" class="edit-btn">
                    취소
                  </ProductListDetailBtn>
                </div>
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard class="full-width">
            <InfoLabel>대여가격</InfoLabel>
            <InfoValue class="price_area">
              <template v-if="!isEditingRentPrice">
                <div class="price-display">
                  <span class="price-text">{{ formattedRentPrice }}원</span>
                  <ProductListDetailBtn @click="toggleRentPriceEditMode" class="edit-btn">
                    가격 수정
                  </ProductListDetailBtn>
                </div>
              </template>
              <template v-else>
                <div class="price-edit-container">
                  <ProductListDetailInput :value="editedRentPrice" @input="onRentPriceInput"
                    @focus="disableBarcodeScanner" @blur="enableBarcodeScanner" type="text" @keyup.enter="updateProduct"
                    class="price-input" />
                  <ProductListDetailBtn @click="updateProduct" class="save-btn">
                    저장
                  </ProductListDetailBtn>
                  <ProductListDetailBtn @click="toggleRentPriceEditMode" class="edit-btn">
                    취소
                  </ProductListDetailBtn>
                </div>
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard class="full-width">
            <InfoLabel>공급가</InfoLabel>
            <InfoValue class="price_area">
              <template v-if="!isEditingSupplyPrice">
                <div class="price-display">
                  <span class="price-text">{{ formattedSupplyPrice }}원</span>
                  <ProductListDetailBtn @click="toggleSupplyPriceEditMode" class="edit-btn">
                    가격 수정
                  </ProductListDetailBtn>
                </div>
              </template>
              <template v-else>
                <div class="price-edit-container">
                  <ProductListDetailInput :value="editedSupplyPrice" @input="onSupplyPriceInput"
                    @focus="disableBarcodeScanner" @blur="enableBarcodeScanner" type="text" @keyup.enter="updateProduct"
                    class="price-input" />
                  <ProductListDetailBtn @click="updateProduct" class="save-btn">
                    저장
                  </ProductListDetailBtn>
                  <ProductListDetailBtn @click="toggleSupplyPriceEditMode" class="edit-btn">
                    취소
                  </ProductListDetailBtn>
                </div>
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoCardHeader>
              <InfoLabel>거래처</InfoLabel>
              <ManufacturerOrderBtn @click="showDevelopmentMessage">제조사 발주</ManufacturerOrderBtn>
            </InfoCardHeader>
            <InfoValue>
              <template v-if="!isEditMode">
                {{ Item.company }}
              </template>
              <template v-else>
                <ProductListDetailInput v-model="editedItem.company" type="text" @focus="disableBarcodeScanner"
                  @blur="enableBarcodeScanner" />
              </template>
            </InfoValue>
          </InfoCard>

          <InfoCard class="full-width">
            <InfoLabel>{{ Item.type === '박스' ? '박스수량' : '품목수량' }}</InfoLabel>
            <InfoValue>
              <QuantityContainer>
                <QuantityBtnArea>
                  <QuantityBtn @click.stop="decreaseQuantity" class="quantity-btn">-</QuantityBtn>
                  <QuantityInput type="number" :value="localQuantity" readonly @click.stop min="0"
                    class="quantity-input" />
                  <QuantityBtn @click.stop="increaseQuantity" class="quantity-btn">+</QuantityBtn>
                </QuantityBtnArea>
                <QuantityBtnArea2>
                  <ProductListDetailBtn @click.stop="decreaseQuantityInput" class="quantity-btn">
                    일괄삭제
                  </ProductListDetailBtn>
                  <ProductListDetailBtn @click.stop="increaseQuantityInput" class="quantity-btn">
                    일괄추가
                  </ProductListDetailBtn>
                </QuantityBtnArea2>
              </QuantityContainer>
            </InfoValue>
          </InfoCard>

          <!-- 박스 타입일 때만 박스당 개수와 낱개수량 표시 -->
          <template v-if="Item.type === '박스'">
            <InfoCard class="full-width">
              <InfoLabel>박스당 개수</InfoLabel>
              <InfoValue>
                <QuantityContainer>
                  <QuantityBtnArea>
                    <QuantityBtn @click.stop="decreasePiecesPerBox" class="quantity-btn">-</QuantityBtn>
                    <QuantityInput type="number" :value="localPiecesPerBox" readonly @click.stop min="0"
                      class="quantity-input" />
                    <QuantityBtn @click.stop="increasePiecesPerBox" class="quantity-btn">+</QuantityBtn>
                  </QuantityBtnArea>
                  <QuantityBtnArea2>
                    <ProductListDetailBtn @click.stop="decreasePiecesPerBoxInput" class="quantity-btn">
                      일괄삭제
                    </ProductListDetailBtn>
                    <ProductListDetailBtn @click.stop="increasePiecesPerBoxInput" class="quantity-btn">
                      일괄추가
                    </ProductListDetailBtn>
                  </QuantityBtnArea2>
                </QuantityContainer>
              </InfoValue>
            </InfoCard>

            <InfoCard class="full-width">
              <InfoLabel>낱개수량</InfoLabel>
              <InfoValue>
                <QuantityContainer>
                  <QuantityBtnArea>
                    <QuantityBtn @click.stop="decreaseQuantityPiece" class="quantity-btn">-</QuantityBtn>
                    <QuantityInput type="number" :value="localPieceQuantity" readonly @click.stop min="0"
                      class="quantity-input" />
                    <QuantityBtn @click.stop="increaseQuantityPiece" class="quantity-btn">+</QuantityBtn>
                  </QuantityBtnArea>
                  <QuantityBtnArea2>
                    <ProductListDetailBtn @click.stop="decreaseQuantityPieceInput" class="quantity-btn">
                      일괄삭제
                    </ProductListDetailBtn>
                    <ProductListDetailBtn @click.stop="increaseQuantityPieceInput" class="quantity-btn">
                      일괄추가
                    </ProductListDetailBtn>
                  </QuantityBtnArea2>
                </QuantityContainer>
              </InfoValue>
            </InfoCard>
          </template>

          <InfoCard class="full-width">
            <InfoLabel>합계수량</InfoLabel>
            <InfoValue class="price_area">
              <div class="price-display">
                <span class="quantity-text">{{ localAllQuantity }}개</span>
              </div>
            </InfoValue>
          </InfoCard>

          <InfoCard class="full-width">
            <InfoLabel>안전재고수량</InfoLabel>
            <InfoValue>
              <QuantityContainer>
                <QuantityBtnArea>
                  <QuantityBtn @click.stop="decreaseSafety" class="quantity-btn">-</QuantityBtn>
                  <QuantityInput type="number" :value="localSafety" readonly @click.stop min="0"
                    class="quantity-input" />
                  <QuantityBtn @click.stop="increaseSafety" class="quantity-btn">+</QuantityBtn>
                </QuantityBtnArea>
                <QuantityBtnArea2>
                  <ProductListDetailBtn @click.stop="decreaseSafetyInput" class="quantity-btn">
                    일괄삭제
                  </ProductListDetailBtn>
                  <ProductListDetailBtn @click.stop="increaseSafetyInput" class="quantity-btn">
                    일괄추가
                  </ProductListDetailBtn>
                </QuantityBtnArea2>
              </QuantityContainer>
            </InfoValue>
          </InfoCard>
        </ProductInfoSection>
      </ProdcutDetailModalContent>

      <!-- * 하단 버튼 영역 -->
      <ProductListDetailBtnContainerEnd>
        <template v-if="!isEditMode">
          <ProductListDetailEditBtn @click="startEdit">
            <Pencil :size="20" />정보 수정
          </ProductListDetailEditBtn>
          <ProductListDetailDeleteBtn @click="confirmDelete">
            <Delete :size="20" />품목 삭제
          </ProductListDetailDeleteBtn>
          <ProductListDetailPlusBtn @click="addOption">
            <Plus :size="20" />옵션 추가
          </ProductListDetailPlusBtn>
        </template>
        <template v-else>
          <ProductListDetailEditBtn @click="saveChanges">
            <ContentSave :size="20" />저장
          </ProductListDetailEditBtn>
          <ProductListDetailDeleteBtn @click="cancelEdit">
            <Close :size="20" />취소
          </ProductListDetailDeleteBtn>
        </template>
      </ProductListDetailBtnContainerEnd>
    </ProductlistDetailModalArea>
  </ProductListDetailArea>

  <!-- * 이미지 검색 모달 -->
  <ImageSearchModal v-if="showImageSearchModal" :initialSearchQuery="editedItem.name3"
    @close="showImageSearchModal = false" @imageSelected="onImageSelected" />
</template>

<script>
import * as ProductListDetailCss from "@/assets/styles/product/ProductDetailCss.js";
import ImageSearchModal from './modal/ImageSearchModal.vue';

import QRCode from "qrcode";
import onScan from "onscan.js";
import Swal from "sweetalert2";

import Pencil from "vue-material-design-icons/Pencil.vue";
import Delete from "vue-material-design-icons/Delete.vue";
import ContentSave from "vue-material-design-icons/ContentSave.vue";
import Close from "vue-material-design-icons/Close.vue";
import Plus from "vue-material-design-icons/Plus.vue";
import BarcodeScan from "vue-material-design-icons/BarcodeScan.vue";
import XIcon from "vue-material-design-icons/Close.vue";
import BarcodeIcon from "vue-material-design-icons/Barcode.vue";
import Camera from "vue-material-design-icons/Camera.vue";

export default {
  name: "ProductDetail",

  emits: ['productDeleted', 'itemUpdated', 'closeModal'],

  components: {
    ...ProductListDetailCss,
    ImageSearchModal,
    Pencil,
    Delete,
    ContentSave,
    Close,
    Plus,
    BarcodeScan,
    XIcon,
    BarcodeIcon,
    Camera
  },

  props: {
    Item: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      localQuantity: this.Item.quantity, // 품목수량 로컬 변수
      localPiecesPerBox: this.Item.pieces_per_box || 0, // 박스당 개수 로컬 변수
      localPieceQuantity: this.Item.piece_quantity || 0, // 낱개수량 로컬 변수
      localAllQuantity: this.Item.all_quantity || 0, // 합계수량 로컬 변수
      localSafety: this.Item.safety || 0, // 안전재고수량 로컬 변수
      qrCodeUrl: null,
      isEditing: false,
      isEditingRentPrice: false,
      editedPrice: "",
      editedRentPrice: "",
      isEditingSupplyPrice: false,
      editedSupplyPrice: "",
      previewImageUrl: null,
      imageKey: Date.now(),
      localPrice: this.Item.price,
      localRentPrice: this.Item.price2,
      localSupplyPrice: this.Item.price3,
      scannedBarcode: this.Item.options?.bcode?.[0] || "",
      stockData: {
        not_release_stock: [],
        release_stock: [],
      },
      stockDataLoaded: false,
      isEditMode: false,
      editedItem: {
        name0: "",
        name1: "",
        name3: "",
        name4: "",
        size: "",
        color: "",
        target: "",
        bcode: "",
        code: "",
        buyprice: "",
        note: "",
        company: "",
        tax: "",
        icode: "",
        hashtags: "",
        all_quantity: "",
      },
      hashtags: [],
      newHashtagInput: "", // 새로운 해시태그 입력 필드
      colorOptions: [],
      sizeOptions: ["S", "M", "L", "XL", "XXL", "XXXL", "소", "중", "대", "특대", "5인치", "6인치", "4mm", "5mm", "6mm", "7mm", "8mm", "1호", "2호", "3호", "4호", "5호", "0.5kg", "1kg", "2kg", "3kg"],
      targetOptions: ["구입", "대여", "구입 또는 대여"],
      taxOptions: ["과세", "비과세"],
      localStatus: "해당사항 없음",
      statusOptions: [
        { value: "해당사항 없음", label: "해당사항 없음" },
        { value: "바코드가 없는 품목", label: "바코드가 없는 품목" },
        { value: "옵션별 바코드가 다른 품목", label: "옵션별 바코드가 다른 품목" },
      ],
      color_obj: {
        value: {}, // 빈 객체로 초기화
      },
      isScannerActive: false,
      // 이미지 검색 관련
      showImageSearchModal: false,
      selectedProductImage: null,
      imageFile: null, // 실제 업로드할 이미지 파일
      imageProcessing: false, // 이미지 처리 중 상태
      showImagePreview: false, // 이미지 미리보기 모달
      allCategories: [], // 전체 카테고리 목록
    };
  },

  computed: {
    formattedPrice() {
      return this.localPrice
        ? this.localPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : "0";
    },
    formattedRentPrice() {
      return this.localRentPrice
        ? this.localRentPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : "0";
    },
    formattedSupplyPrice() {
      return this.localSupplyPrice
        ? this.localSupplyPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : "0";
    },
    displayHashtags() {
      // 해시태그 배열이 존재하는지 확인
      if (!this.hashtags) return [];

      // 배열에서 중복 제거하고 반환
      return Array.from(new Set(this.hashtags));
    },
    // 카테고리1에 따른 카테고리2 옵션 필터링
    filteredSubCategories() {
      if (!this.editedItem.name0 || !this.allCategories.length) {
        return [];
      }

      // 선택된 카테고리1 찾기
      const selectedCategory = this.allCategories.find(category => category.name === this.editedItem.name0);

      if (selectedCategory && selectedCategory.subcategories) {
        return selectedCategory.subcategories;
      }

      return [];
    },
  },

  methods: {
    // 카테고리 데이터 가져오기 메서드
    async fetchAllCategories() {
      try {
        const response = await this.$axios.get(
          `${this.$apiUrls.hanqmedi}/getAllCategories`,
          {
            params: {
              companykey: this.$globalState.company_key
            }
          }
        );

        if (response.data.success) {
          this.allCategories = response.data.data;
        }
      } catch (error) {
        console.error('전체 카테고리 데이터 조회 실패:', error);
        this.$swal.fire({
          title: '오류',
          text: '카테고리 데이터를 불러오는데 실패했습니다.',
          icon: 'error',
          confirmButtonColor: '#fb8b24'
        });
      }
    },

    // 카테고리1 변경 시 카테고리2 초기화
    onCategory1Change() {
      // 카테고리1이 변경되면 카테고리2를 초기화
      this.editedItem.name1 = '';
    },

    // 이미지 검색 모달 열기
    searchProductImages() {
      if (!this.editedItem.name3.trim()) {
        this.$swal.fire('알림', '품목명을 입력해주세요.', 'warning');
        return;
      }

      this.showImageSearchModal = true;
    },

    // 이미지 검색 모달에서 이미지 선택 완료
    async onImageSelected(selectedImageData) {
      try {
        this.imageProcessing = true;

        // 즉시 미리보기 이미지 설정 (기존 previewImageUrl 활용)
        if (this.previewImageUrl) {
          URL.revokeObjectURL(this.previewImageUrl);
        }

        // 선택한 이미지를 blob으로 변환하여 미리보기 URL 생성
        try {
          const response = await fetch(selectedImageData.url);
          const blob = await response.blob();
          this.previewImageUrl = URL.createObjectURL(blob);
        } catch (fetchError) {
          console.log('미리보기 생성 실패, 원본 URL 사용:', fetchError);
          // 네트워크 오류 시 원본 URL을 임시로 사용 (CORS 문제가 있을 수 있음)
        }

        // 강제 리렌더링하여 즉시 이미지 반영
        this.$forceUpdate();

        // 로딩 표시
        const loadingSwal = this.$swal.fire({
          title: '이미지 처리 중',
          text: '선택한 이미지를 처리하고 있습니다...',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            this.$swal.showLoading();
          }
        });

        // FormData 생성
        const formData = new FormData();

        // 이미지 URL과 관련 정보 추가
        formData.append('imageUrl', selectedImageData.url);
        formData.append('imageTitle', selectedImageData.title || '');
        formData.append('productId', this.Item.num);
        formData.append('identifier', this.Item.code === this.Item.num ? this.Item.num : this.Item.code || this.Item.num);
        formData.append('updateExisting', 'true');
        formData.append('companykey', this.$globalState.company_key);

        // 현재 입력된 모든 데이터를 함께 전송하여 다른 필드가 리셋되지 않도록 함
        if (this.isEditMode) {
          // 편집 모드인 경우 editedItem의 모든 데이터 포함
          Object.keys(this.editedItem).forEach((key) => {
            if (key !== 'location1' && key !== 'location2') {
              formData.append(key, this.editedItem[key] || '');
            }
          });

          // location 처리
          if (this.editedItem.location1 || this.editedItem.location2) {
            const location = `${this.editedItem.location1 || ''}-${this.editedItem.location2 || ''}`;
            formData.append('location', location);
          } else {
            formData.append('location', '');
          }

          // 해시태그 처리
          const uniqueHashtags = [...new Set(this.hashtags)]
            .filter((tag) => tag.trim())
            .join(",");
          formData.append('hashtags', uniqueHashtags);
        } else {
          // 편집 모드가 아닌 경우 현재 Item의 데이터 사용
          formData.append('name0', this.Item.name0 || '');
          formData.append('name1', this.Item.name1 || '');
          formData.append('name3', this.Item.name3 || '');
          formData.append('name4', this.Item.name4 || '');
          formData.append('target', this.Item.target || '');
          formData.append('bcode', this.Item.options?.bcode?.[0] || '');
          formData.append('code', this.Item.code || '');
          formData.append('color', this.Item.color || '');
          formData.append('size', this.Item.size || '');
          formData.append('note', this.Item.note || '');
          formData.append('company', this.Item.company || '');
          formData.append('tax', this.Item.tax || '');
          formData.append('icode', this.Item.icode || '');
          formData.append('location', this.Item.location || '');

          // 해시태그 처리
          const hashtagString = Array.isArray(this.Item.hashtags)
            ? Array.from(new Set(this.Item.hashtags)).join(",")
            : this.Item.hashtags
              ? Array.from(
                new Set(
                  this.Item.hashtags
                    .split(",")
                    .map((tag) => tag.trim())
                    .filter((tag) => tag)
                )
              ).join(",")
              : "";
          formData.append('hashtags', hashtagString);
        }

        // 기존 updateProducts API 사용
        const response = await this.$axios.post(
          `${this.$apiUrls.hanqmedi}/updateProducts/${this.Item.num}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        // 로딩 종료
        loadingSwal.close();

        if (response.data.success) {
          // 이미지 강제 새로고침을 위해 키 업데이트 (S3 이미지로 전환)
          this.imageKey = Date.now();

          // 미리보기 URL 정리 (이제 S3 이미지를 사용)
          if (this.previewImageUrl) {
            URL.revokeObjectURL(this.previewImageUrl);
            this.previewImageUrl = null;
          }

          // 성공 메시지
          await this.$swal.fire({
            title: '성공',
            text: '이미지가 성공적으로 등록되었습니다.',
            icon: 'success',
            confirmButtonText: '확인'
          });

          // 편집 모드인 경우 업데이트된 정보로 현재 아이템 정보 갱신
          if (this.isEditMode) {
            const updatedItem = {
              ...this.Item,
              name0: this.editedItem.name0,
              name1: this.editedItem.name1,
              name3: this.editedItem.name3,
              name4: this.editedItem.name4,
              size: this.editedItem.size,
              color: this.editedItem.color,
              target: this.editedItem.target,
              code: this.editedItem.code,
              note: this.editedItem.note,
              company: this.editedItem.company,
              tax: this.editedItem.tax,
              icode: this.editedItem.icode,
              location: this.editedItem.location1 + "-" + this.editedItem.location2,
              hashtags: [...new Set(this.hashtags)].filter((tag) => tag.trim()).join(","),
              options: {
                ...this.Item.options,
                bcode: [this.editedItem.bcode],
                color: [this.editedItem.color],
                size: [this.editedItem.size],
              },
              imageUpdateTime: this.imageKey,
            };

            // 부모 컴포넌트에 업데이트 알림
            this.$emit('itemUpdated', updatedItem);
          } else {
            // 부모 컴포넌트에 이미지 업데이트만 알림
            this.$emit('itemUpdated', {
              ...this.Item,
              imageUpdateTime: this.imageKey,
            });
          }

        } else {
          // 실패 시 미리보기 이미지 제거
          if (this.previewImageUrl) {
            URL.revokeObjectURL(this.previewImageUrl);
            this.previewImageUrl = null;
          }

          throw new Error(response.data.message || '이미지 업로드에 실패했습니다.');
        }

      } catch (error) {
        console.error('이미지 선택 처리 중 오류:', error);

        // 오류 시 미리보기 이미지 제거
        if (this.previewImageUrl) {
          URL.revokeObjectURL(this.previewImageUrl);
          this.previewImageUrl = null;
        }

        await this.$swal.fire({
          title: '오류',
          text: error.response?.data?.message || error.message || '이미지 처리 중 오류가 발생했습니다.',
          icon: 'error',
          confirmButtonText: '확인'
        });
      } finally {
        this.imageProcessing = false;
      }
    },

    async addHashtag() {
      if (!this.newHashtagInput.trim()) return;

      // 입력된 해시태그를 개별로 분리
      const newTags = this.newHashtagInput
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      if (newTags.length === 0) return;

      // 기존 해시태그와 중복되지 않는 태그만 필터링
      const uniqueNewTags = newTags.filter((tag) => !this.hashtags.includes(tag));

      if (uniqueNewTags.length === 0) {
        await this.$swal.fire({
          title: "중복된 해시태그",
          text: "이미 등록된 해시태그입니다.",
          icon: "warning",
          confirmButtonText: "확인",
        });
        return;
      }

      // 중복되지 않은 태그만 추가
      this.hashtags = [...this.hashtags, ...uniqueNewTags];

      // editedItem의 hashtags 업데이트
      this.editedItem.hashtags = this.hashtags.join(",");

      // 입력 필드 초기화
      this.newHashtagInput = "";
    },
    deleteHashtag(index) {
      // 해당 인덱스의 해시태그 제거
      this.hashtags.splice(index, 1);

      // editedItem의 hashtags 업데이트
      this.editedItem.hashtags = this.hashtags.join(",");
    },
    async deleteImage() {
      try {
        const result = await this.$swal.fire({
          title: "이미지 삭제",
          text: "정말로 이 이미지를 삭제하시겠습니까?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "삭제",
          cancelButtonText: "취소",
        });

        if (!result.isConfirmed) return;

        const response = await this.$axios.post(
          `${this.$apiUrls.hanqmedi}/deleteProductImage`,
          {
            productId: this.Item.num,
            identifier:
              this.Item.code === this.Item.num
                ? this.Item.num
                : this.Item.code || this.Item.num,
            companykey: this.$globalState.company_key,
          }
        );

        // 응답 처리 수정
        if (response.data.success) {
          await this.$swal.fire({
            title: "성공",
            text: response.data.message,
            icon: "success",
            confirmButtonText: "확인",
          });

          this.imageKey = Date.now();
          this.$emit("itemUpdated", {
            ...this.Item,
            imageUpdateTime: this.imageKey,
          });

          if (this.previewImageUrl) {
            URL.revokeObjectURL(this.previewImageUrl);
            this.previewImageUrl = null;
          }
        } else {
          throw new Error(response.data.message);
        }
      } catch (error) {
        console.error("이미지 삭제 중 오류:", error);
        await this.$swal.fire({
          title: "오류",
          text:
            error.response?.data?.message ||
            error.message ||
            "이미지 삭제 중 오류가 발생했습니다.",
          icon: "error",
          confirmButtonText: "확인",
        });
      }
    },
    async getColorTable() {
      try {
        const response = await this.$axios.post(
          `${this.$apiUrls.hanq}/ColorTableGet`,
          {}
        );
        const data = response.data;
        if (data.code === 200) {
          const newColorObj = {};
          data.data.forEach((obj) => {
            newColorObj[String(obj.num)] = obj.name;
          });
          this.color_obj.value = newColorObj;
          // colorOptions 배열에 name 값들만 저장
          this.colorOptions = data.data.map((color) => color.name);
        }
      } catch (error) {
        console.error("색상 테이블 가져오기 실패:", error);
      }
    },

    // === 편집 모드 관련 메서드 ===
    startEdit() {
      this.isEditMode = true;
      this.initializeEditedItem();
      this.disableBarcodeScanner();
    },
    cancelEdit() {
      this.isEditMode = false;
      this.initializeEditedItem();
      this.enableBarcodeScanner();
    },
    // 편집할 아이템 초기화
    initializeEditedItem() {
      if (this.Item) {
        // location을 '-'로 분리
        const locationParts = this.Item.location
          ? this.Item.location.split("-")
          : ["", ""];
        const location1 = locationParts[0] ? locationParts[0].trim() : "";
        const location2 = locationParts[1] ? locationParts[1].trim() : "";

        // 기존 데이터를 깊은 복사로 초기화
        this.editedItem = {
          name0: this.Item.name0 || "",
          name1: this.Item.name1 || "",
          name3: this.Item.name3 || "",
          name4: this.Item.name4 || "",
          code: this.Item.code || "",
          target: this.Item.target || "",
          size: this.Item.size || "",
          color: this.Item.color || "",
          bcode: this.Item.options?.bcode?.[0] || "",
          tax: this.Item.tax || "",
          icode: this.Item.icode || "",
          location1: location1,
          location2: location2,
          note: this.Item.note || "",
          company: this.Item.company || "",
          buyprice: this.Item.buyprice || "",
          price: this.Item.price || "",
          status: this.Item.status || "",
          hashtags: Array.isArray(this.Item.hashtags)
            ? Array.from(new Set(this.Item.hashtags)).join(",")
            : this.Item.hashtags
              ? Array.from(
                new Set(
                  this.Item.hashtags
                    .split(",")
                    .map((tag) => tag.trim())
                    .filter((tag) => tag)
                )
              ).join(",")
              : "",
        };
      }
    },
    async saveChanges() {
      try {
        const formData = new FormData();

        // 이미지 파일이 선택된 경우
        const imageFile = this.$refs.imageInput?.files[0];
        if (imageFile) {
          formData.append("image", imageFile);
          // 기존 이미지 정보 추가
          const identifier =
            this.Item.code === this.Item.num
              ? this.Item.num
              : this.Item.code || this.Item.num;
          formData.append("productId", this.Item.num);
          formData.append("identifier", identifier);
          formData.append("updateExisting", "true");
        }

        // 합계수량 값을 localAllQuantity에서 가져와 editedItem에 설정
        this.editedItem.all_quantity = Number(this.localAllQuantity);

        // 박스당 개수 값 추가
        formData.append("pieces_per_box", Number(this.localPiecesPerBox));

        // 해시태그 처리 - 중복 제거
        const uniqueHashtags = [...new Set(this.hashtags)]
          .filter((tag) => tag.trim())
          .join(",");

        formData.append("hashtags", uniqueHashtags);

        // location1과 location2를 처리하여 location으로 추가
        if (this.editedItem.location1 || this.editedItem.location2) {
          const location = `${this.editedItem.location1 || ""}-${this.editedItem.location2 || ""
            }`;
          formData.append("location", location);
        } else {
          formData.append("location", "");
        }

        // editedItem의 데이터를 FormData에 추가
        Object.keys(this.editedItem).forEach((key) => {
          if (key !== "hashtags" && key !== "location1" && key !== "location2") {
            // 수량 관련 필드는 명시적으로 숫자로 변환
            if (key === "quantity" || key === "piece_quantity" || key === "all_quantity") {
              formData.append(key, Number(this.editedItem[key]));
            } else {
              formData.append(key, this.editedItem[key]);
            }
          }
        });

        formData.append("companykey", this.$globalState.company_key);

        // 품목 정보 업데이트
        const response = await this.$axios.post(
          `${this.$apiUrls.hanqmedi}/updateProducts/${this.Item.num}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.success) {
          // 이미지 강제 새로고침을 위해 키 업데이트
          this.imageKey = Date.now();

          // 미리보기 URL 제거
          if (this.previewImageUrl) {
            URL.revokeObjectURL(this.previewImageUrl);
            this.previewImageUrl = null;
          }

          // 성공 메시지 표시
          await this.$swal.fire({
            title: "성공",
            text: "품목이 성공적으로 수정되었습니다.",
            icon: "success",
            confirmButtonText: "확인",
          });

          const updatedItem = {
            ...this.Item,
            name0: this.editedItem.name0,
            name1: this.editedItem.name1,
            name3: this.editedItem.name3,
            name4: this.editedItem.name4,
            size: this.editedItem.size,
            color: this.editedItem.color,
            target: this.editedItem.target,
            code: this.editedItem.code,
            price: this.editedItem.price,
            buyprice: this.editedItem.buyprice,
            note: this.editedItem.note,
            company: this.editedItem.company,
            tax: this.editedItem.tax,
            status: this.editedItem.status,
            icode: this.editedItem.icode,
            location: this.editedItem.location1 + "-" + this.editedItem.location2,
            pieces_per_box: this.localPiecesPerBox,
            all_quantity: this.localAllQuantity,
            hashtags: uniqueHashtags,
            options: {
              ...this.Item.options,
              bcode: [this.editedItem.bcode],
              color: [this.editedItem.color],
              size: [this.editedItem.size],
            },
          };

          // 수정 모드 종료
          this.isEditMode = false;

          this.enableBarcodeScanner();

          // 이미지 업로드 후 화면 새로고침
          if (imageFile) {
            this.$forceUpdate();
          }

          // 부모 컴포넌트에 업데이트된 아이템 전달
          this.$emit("itemUpdated", updatedItem);
        }
      } catch (error) {
        console.error("품목 수정 중 오류:", error);

        // 서버에서 반환된 에러 메시지 확인
        const errorMessage = error.response?.data?.message || "품목 수정 중 오류가 발생했습니다.";

        await this.$swal.fire({
          title: "오류",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "확인",
        });
      }
    },
    async addOption() {
      this.disableBarcodeScanner();

      // 색상 테이블이 없으면 먼저 가져오기
      if (!this.color_obj.value || Object.keys(this.color_obj.value).length === 0) {
        await this.getColorTable();
      }

      const swal = this.$swal;

      // 먼저 추가할 옵션 종류 선택
      const { value: optionType } = await swal.fire({
        title: "추가할 옵션 선택",
        input: "select",
        inputOptions: {
          size: "사이즈",
          color: "색상",
          custom: "옵션 추가",
        },
        inputPlaceholder: "옵션을 선택하세요",
        showCancelButton: true,
        confirmButtonText: "다음",
        cancelButtonText: "취소",
      });

      this.enableBarcodeScanner();

      if (!optionType) return;

      // 선택된 옵션 타입에 따른 입력 폼 표시
      const { value: formValues } = await swal.fire({
        title:
          optionType === "custom"
            ? "옵션 추가"
            : optionType === "size"
              ? "사이즈 옵션 추가"
              : "색상 옵션 추가",
        html:
          optionType === "custom"
            ? `
                    <div style="margin-bottom: 15px;">
                        <div style="display: flex; align-items: center;">
                            <label for="option-name1" style="width: 80px;">옵션명 1</label>
                            <input id="option-name1" class="swal2-input" style="flex: 1;" placeholder="추가할 옵션명을 입력해주세요.">
                        </div>
                        <div style="display: flex; align-items: center;">
                            <label for="option-value1" style="width: 80px;">옵션값 1</label>
                            <input id="option-value1" class="swal2-input" style="flex: 1;" placeholder="추가할 옵션값을 입력해주세요.">
                        </div>
                    </div>
                    <div>
                        <div style="display: flex; align-items: center;">
                            <label for="option-name2" style="width: 80px;">옵션명 2</label>
                            <input id="option-name2" class="swal2-input" style="flex: 1;" placeholder="추가할 옵션명을 입력해주세요.">
                        </div>
                        <div style="display: flex; align-items: center;">
                            <label for="option-value2" style="width: 80px;">옵션값 2</label>
                            <input id="option-value2" class="swal2-input" style="flex: 1;" placeholder="추가할 옵션값을 입력해주세요.">
                        </div>
                    </div>
                `
            : `
                    <div id="swal-content">
                        <div style="display: flex; align-items: center;">
                            <label for="swal-input" style="width: 60px; text-align: left;">
                                ${optionType === "size" ? "사이즈" : "색상"}
                            </label>
                            <input 
                                id="swal-input" 
                                class="swal2-input" 
                                style="flex: 1;" 
                                ${optionType === "size"
              ? 'oninput="this.value = this.value.toUpperCase()"'
              : ""
            }
                                placeholder="${optionType === "size" ? "사이즈 입력" : "색상 입력"
            }"
                            >
                        </div>
                        <div style="margin-top: 10px;">
                            <div style="display: flex; flex-wrap: wrap; gap: 8px; padding: 8px;">
                                ${(optionType === "size"
              ? this.sizeOptions
              : Object.values(this.color_obj.value || {})
            )
              .map(
                (option) => `
                                    <span 
                                        onclick="document.getElementById('swal-input').value='${option}'"
                                        style="
                                            display: inline-block;
                                            padding: 4px 8px;
                                            background: #f0f0f0;
                                            border-radius: 15px;
                                            font-size: 13px;
                                            color: #666;
                                            cursor: pointer;
                                            transition: all 0.2s;
                                            margin: 2px;
                                        "
                                        onmouseover="this.style.background='#ffc971'"
                                        onmouseout="this.style.background='#f0f0f0'"
                                    >#${option}</span>
                                `
              )
              .join("")}
                            </div>
                        </div>
                    </div>
                `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "추가",
        cancelButtonText: "취소",
        preConfirm: () => {
          if (optionType === "custom") {
            const name1 = document.getElementById("option-name1").value.trim();
            const value1 = document.getElementById("option-value1").value.trim();
            const name2 = document.getElementById("option-name2").value.trim();
            const value2 = document.getElementById("option-value2").value.trim();

            if (!name1 || !value1) {
              const validationMessage = "최소 하나의 옵션을 입력해주세요.";
              Swal.showValidationMessage(validationMessage);
              const validationMessageElement = Swal.getPopup().querySelector(
                ".swal2-validation-message"
              );
              if (validationMessageElement) {
                validationMessageElement.style.width = "400px";
                validationMessageElement.style.margin = "0 auto"; // 중앙 정렬
                validationMessageElement.style.backgroundColor = "#ffeccc";
                validationMessageElement.style.color = "#000";
                validationMessageElement.style.fontWeight = "bold";
              }
              return false;
            }

            return {
              customOptions: [
                { name: name1, value: value1 },
                name2 && value2 ? { name: name2, value: value2 } : null,
              ].filter(Boolean),
            };
          } else {
            const value = document.getElementById("swal-input").value.trim();
            if (!value) {
              const validationMessage = `${optionType === "size" ? "사이즈를" : "색상을"
                } 입력해주세요.`;
              Swal.showValidationMessage(validationMessage);
              const validationMessageElement = Swal.getPopup().querySelector(
                ".swal2-validation-message"
              );
              if (validationMessageElement) {
                validationMessageElement.style.width = "400px";
                validationMessageElement.style.margin = "0 auto"; // 중앙 정렬
                validationMessageElement.style.backgroundColor = "#ffeccc";
                validationMessageElement.style.color = "#000";
                validationMessageElement.style.fontWeight = "bold";
              }
              return false;
            }
            return { [optionType]: value };
          }
        },
      });

      if (formValues) {
        await swal.fire({
          icon: 'info',
          title: '개발 진행중',
          text: '옵션 추가 기능은 현재 개발 중입니다.',
          confirmButtonText: '확인'
        });

        // try {
        //   const payload = {
        //     b_item_id: this.Item.num,
        //     companykey: this.$globalState.company_key,
        //     ...(optionType === "custom"
        //       ? { customOptions: formValues.customOptions }
        //       : formValues),
        //   };

        //   const response = await this.$axios.post(
        //     `${this.$apiUrls.hanqmedi}/addProductOption`,
        //     payload
        //   );
        //   // 응답 데이터 확인
        //   console.log("서버 응답:", response.data);

        //   switch (response.data.statusCode) {
        //     case 200:
        //       // 성공적으로 옵션이 추가된 경우
        //       await swal.fire({
        //         title: "성공",
        //         text: response.data.message,
        //         icon: "success",
        //         confirmButtonText: "확인",
        //       });

        //       // 부모 컴포넌트에 업데이트 이벤트 발생
        //       this.$emit("optionAdded");

        //       // 현재 컴포넌트 데이터 새로고침
        //       await this.getProductsDetail();
        //       break;

        //     case 400:
        //     case 404:
        //     case 409:
        //       // 비즈니스 로직 실패 또는 기타 문제
        //       await swal.fire({
        //         title: "알림",
        //         text: response.data.message,
        //         icon: "warning",
        //         confirmButtonText: "확인",
        //       });
        //       break;

        //     default:
        //       // 기타 상태 코드
        //       await swal.fire({
        //         title: "알림",
        //         text: response.data.message || "옵션 추가에 실패했습니다.",
        //         icon: "warning",
        //         confirmButtonText: "확인",
        //       });
        //       break;
        //   }
        // } catch (error) {
        //   console.error("옵션 추가 중 오류:", error);

        //   // 서버 에러 응답 처리
        //   const errorResponse = error.response?.data;

        //   await swal.fire({
        //     title: "오류",
        //     text: errorResponse?.message || "옵션 추가 중 오류가 발생했습니다.",
        //     icon: "error",
        //     confirmButtonText: "확인",
        //   });
        // }
      }
    },

    // === 이미지 처리 관련 메서드 ===
    // 이미지 선택 시 미리보기 처리
    async handleImageSelect(event) {
      const file = event.target.files[0];
      if (file) {
        try {
          // 이미지 미리보기 URL 생성
          if (this.previewImageUrl) {
            URL.revokeObjectURL(this.previewImageUrl);
          }
          this.previewImageUrl = URL.createObjectURL(file);

          // 이미지 키 업데이트
          this.imageKey = Date.now();

          // FormData 생성 및 이미지 즉시 업로드
          const formData = new FormData();
          formData.append("image", file);
          formData.append("productId", this.Item.num);
          formData.append(
            "identifier",
            this.Item.code === this.Item.num
              ? this.Item.num
              : this.Item.code || this.Item.num
          );
          formData.append("updateExisting", "true");
          formData.append("companykey", this.$globalState.company_key);

          // 이미지 업로드 API 호출
          const response = await this.$axios.post(
            `${this.$apiUrls.hanqmedi}/updateProducts/${this.Item.num}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (response.data.success) {
            // 부모 컴포넌트에 이미지 업데이트 알림
            this.$emit("itemUpdated", {
              ...this.Item,
              imageUpdateTime: this.imageKey,
            });

            // 강제 리렌더링
            this.$forceUpdate();
          }
        } catch (error) {
          console.error("이미지 처리 중 오류:", error);
          await this.$swal.fire({
            title: "오류",
            text: "이미지 업로드 중 오류가 발생했습니다.",
            icon: "error",
            confirmButtonText: "확인",
          });
        }
      }
    },

    getImageUrl(item) {
      // 미리보기 이미지가 있으면 먼저 보여줌
      if (this.previewImageUrl) {
        return this.previewImageUrl;
      }

      if (!item || !item.num) {
        return "/icon.png";
      }

      const s3BaseUrl = this.$apiUrls.s3Bucket;

      // 캐시 무효화를 위해 imageKey가 업데이트된 경우 타임스탬프 추가
      const cacheBuster = this.imageKey && this.imageKey !== Date.now() ? `?t=${this.imageKey}` : '';

      return `${s3BaseUrl}/Product-image/${item.num}.jpg${cacheBuster}`;
    },

    handleImageError(e) {
      const currentSrc = e.target.src;
      const s3BaseUrl = this.$apiUrls.s3Bucket;

      // 기본 이미지로 이미 변경된 경우 중단
      if (currentSrc.includes("/icon.png")) {
        return;
      }

      // 시도할 확장자 목록
      const extensions = ['jpg', 'jpeg', 'png'];

      // 현재 파일명과 확장자 파싱
      const urlParts = currentSrc.split("/");
      const fileNameWithExt = urlParts[urlParts.length - 1];
      const fileNamePart = fileNameWithExt.split(".")[0].split("?")[0];
      const currentExt = fileNameWithExt.split(".")[1]?.split("?")[0];

      // 현재 이미지가 this.Item.num 기반인지 확인
      const isUsingNum = String(fileNamePart) === String(this.Item.num);

      if (!isUsingNum) {
        // num으로 시도하지 않았다면 첫 번째 확장자로 시작
        e.target.src = `${s3BaseUrl}/Product-image/${this.Item.num}.${extensions[0]}`;
        return;
      }

      // 현재 확장자가 무엇인지 확인하고 다음 확장자로 전환
      const currentExtIndex = extensions.indexOf(currentExt);
      const nextExtIndex = currentExtIndex + 1;

      if (nextExtIndex < extensions.length) {
        // 다음 확장자 시도
        e.target.src = `${s3BaseUrl}/Product-image/${this.Item.num}.${extensions[nextExtIndex]}`;
      } else {
        // 모든 확장자 실패 → 기본 이미지로
        e.target.src = "/icon.png";
      }
    },

    // === 재고 상태 관련 메서드 ===
    getStockTypeTitle(stockType) {
      const titles = {
        not_release_stock: "미출고",
        release_stock: "출고",
      };
      return titles[stockType] || stockType;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("ko-KR");
    },
    async updateStatus() {
      try {
        const response = await this.$axios.post(
          `${this.$apiUrls.hanqmedi}/stockStatusUpdate`,
          {
            b_item_id: this.Item.num,
            status: this.localStatus,
          }
        );

        if (response.data.statusCode === 200) {
          this.$swal.fire({
            title: "성공",
            text: "품목 상태가 수정되었습니다.",
            icon: "success",
            confirmButtonText: "확인",
          });
        }
      } catch (error) {
        console.error("Status update error:", error);
        this.$swal.fire({
          title: "오류",
          text: "품목 상태 수정 중 오류가 발생했습니다.",
          icon: "error",
          confirmButtonText: "확인",
        });
      }
    },
    getStatusText(statusCode) {
      const statusMap = {
        0: "해당사항 없음",
        1: "바코드가 없는 품목",
        2: "옵션별 바코드가 다른 품목",
        3: "바코드가 2개 이상인 품목",
        4: "바코드 인식이 되지 않는 품목",
      };

      const numericStatus = Number(statusCode);
      const mappedStatus = statusMap[numericStatus];

      return mappedStatus || "해당사항 없음";
    },

    async getProductsDetail() {
      try {
        const response = await this.$axios.post(
          `${this.$apiUrls.hanqmedi}/getProductsDetail`,
          {
            b_item_id: this.Item.num,
            companykey: this.$globalState.company_key,
          }
        );

        // productStatus 값이 있는 경우 상태 업데이트
        if (response.data.statusCode === 200) {
          // 응답 데이터에서 직접 safety 값을 가져와 설정
          if (response.data.safety !== undefined) {
            this.localSafety = parseInt(response.data.safety) || 0;
            this.Item.safety = this.localSafety;
          }

          if (response.data.response && response.data.response.length > 0) {
            this.stockData = { ...this.stockData, ...response.data.response[0] };
          }

          // 상태 처리
          const status = response.data.productStatus;
          this.localStatus = this.getStatusText(status);

          // 낱개수량 처리
          if (response.data.piece_quantity !== undefined) {
            this.localPieceQuantity = parseInt(response.data.piece_quantity) || 0;
          }

          // 해시태그 데이터 처리
          if (response.data.hashtags) {
            if (Array.isArray(response.data.hashtags)) {
              // 배열 내 문자열을 개별 해시태그로 분리
              this.hashtags = response.data.hashtags.flatMap((tag) =>
                tag
                  .split(",")
                  .map((t) => t.trim())
                  .filter((t) => t)
              );
            } else if (typeof response.data.hashtags === "string") {
              // 문자열인 경우 쉼표로 분리
              this.hashtags = response.data.hashtags
                .split(",")
                .map((tag) => tag.trim())
                .filter((tag) => tag);
            } else {
              this.hashtags = [];
            }
          } else {
            this.hashtags = [];
          }

          this.stockDataLoaded = true;
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
      }
    },

    // =======================================================================
    // 수량 관리 관련 메서드
    // =======================================================================

    // 박스당 개수 관련 메서드 =================================================
    async decreasePiecesPerBox() {
      if (this.localPiecesPerBox > 1) {
        await this.updatePiecesPerBox(this.localPiecesPerBox - 1, false); // false = 메시지 표시 안함
      }
    },

    async increasePiecesPerBox() {
      await this.updatePiecesPerBox(this.localPiecesPerBox + 1, false); // false = 메시지 표시 안함
    },

    async updatePiecesPerBox(value, showMessage = true) {
      // 입력값을 정수로 확실하게 변환
      const newValue = parseInt(value || 0);

      if (newValue < 1) {
        if (showMessage) {
          await this.$swal.fire({
            title: "입력 오류",
            text: "박스당 개수는 1 이상이어야 합니다.",
            icon: "warning",
            confirmButtonText: "확인",
          });
        }
        return;
      }

      try {
        // 현재 값과 새 값을 로그로 출력
        console.log(`박스당 개수 업데이트 요청: ${this.localPiecesPerBox} → ${newValue}`);

        const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/updateProductQuantities`, {
          productId: this.Item.num,
          pieces_per_box: newValue,
          companykey: this.$globalState.company_key
        });

        if (response.data.success) {
          // 성공 시 로컬 상태 업데이트
          this.localPiecesPerBox = newValue;

          // 합계 수량 업데이트
          this.updateAllQuantity();

          console.log(`박스당 개수 업데이트 성공: 현재 값 = ${this.localPiecesPerBox}, 합계: ${this.localAllQuantity}`);

          if (showMessage) {
            await this.$swal.fire({
              title: "성공",
              text: "박스당 개수가 수정되었습니다.",
              icon: "success",
              confirmButtonText: "확인",
            });
          }

          // 부모 컴포넌트에 업데이트 알림
          this.$emit("itemUpdated", {
            ...this.Item,
            pieces_per_box: newValue,
            all_quantity: this.localAllQuantity
          });
        } else {
          if (showMessage) {
            throw new Error(response.data.message || "박스당 개수 수정에 실패했습니다.");
          }
        }
      } catch (error) {
        console.error("박스당 개수 수정 실패:", error);
        if (showMessage) {
          await this.$swal.fire({
            title: "오류",
            text: error.response?.data?.message || "박스당 개수 수정 중 오류가 발생했습니다.",
            icon: "error",
            confirmButtonText: "확인",
          });
        }
      }
    },

    async decreasePiecesPerBoxInput() {
      const result = await this.$swal.fire({
        title: "일괄삭제 수량 입력",
        input: "number",
        inputAttributes: {
          autocapitalize: "off",
          required: true,
          minlength: 1,
          maxlength: 10,
          step: 1,
          min: 0,
          type: "number",
        },
        confirmButtonText: "확인",
        cancelButtonText: "취소",
        showCancelButton: true,
      });

      if (result.isConfirmed) {
        const quantity = parseInt(result.value, 10);
        if (!isNaN(quantity) && quantity > 0) {
          // 현재 수량에서 입력한 수량을 뺌 (최소 1이 되도록 Math.max 사용)
          const currentQuantity = parseInt(this.localPiecesPerBox || 1);
          const newValue = Math.max(1, currentQuantity - quantity);

          // 감소할 수량이 현재 수량보다 많은 경우 경고
          if (quantity > currentQuantity - 1) { // -1은 최소값 1을 유지하기 위함
            await this.$swal.fire({
              title: "경고",
              text: `감소할 수량(${quantity})이 너무 큽니다. 박스당 개수는 최소 1개여야 합니다.`,
              icon: "warning",
              confirmButtonText: "확인",
            });
          }

          await this.updatePiecesPerBox(newValue, true); // true = 메시지 표시
        }
      }
    },

    async increasePiecesPerBoxInput() {
      const result = await this.$swal.fire({
        title: "일괄추가 수량 입력",
        input: "number",
        inputAttributes: {
          autocapitalize: "off",
          required: true,
          minlength: 1,
          maxlength: 10,
          step: 1,
          min: 0,
          type: "number",
        },
        confirmButtonText: "확인",
        cancelButtonText: "취소",
        showCancelButton: true,
      });

      if (result.isConfirmed) {
        const quantity = parseInt(result.value, 10);
        if (!isNaN(quantity) && quantity > 0) {
          // 현재 수량에 입력한 수량을 더함
          const currentQuantity = parseInt(this.localPiecesPerBox || 1);
          const newValue = currentQuantity + quantity;

          await this.updatePiecesPerBox(newValue, true); // true = 메시지 표시
        }
      }
    },

    // 합계 수량 관련 메서드 ==================================================
    updateAllQuantity() {
      const quantity = parseInt(this.localQuantity || 0);
      const pieceQuantity = parseInt(this.localPieceQuantity || 0);
      const piecesPerBox = parseInt(this.localPiecesPerBox || 0);

      if (this.Item.type === '박스') {
        // 박스 타입인 경우: (박스 수량 * 박스당 개수) + 낱개 수량
        const boxTotal = quantity * piecesPerBox;
        this.localAllQuantity = boxTotal + pieceQuantity;
      } else {
        // 낱개 타입인 경우: 품목 수량이 그대로 합계 수량
        this.localAllQuantity = quantity;
      }
    },

    async decreaseQuantity() {
      if (this.localQuantity > 0) {
        this.decreaseQuantityupdate(1);
      }
    },

    async increaseQuantity() {
      this.updateQuantity(1);
    },

    async decreaseQuantityInput() {
      this.$swal
        .fire({
          title: "일괄삭제 수량 입력",
          input: "number",
          inputAttributes: {
            autocapitalize: "off",
            required: true,
            minlength: 1,
            maxlength: 10,
            step: 1,
            min: 0,
            type: "number",
          },
          confirmButtonText: "확인",
          cancelButtonText: "취소",
          showCancelButton: true,
        })
        .then((result) => {
          if (result.isConfirmed) {
            const quantity = parseInt(result.value, 10);
            if (!isNaN(quantity) && quantity > 0) {
              // 감소 함수 호출
              this.decreaseQuantityupdate(quantity);
            }
          }
        });
    },

    async increaseQuantityInput() {
      this.$swal
        .fire({
          title: "일괄추가 수량 입력",
          input: "number",
          inputAttributes: {
            autocapitalize: "off",
            required: true,
            minlength: 1,
            maxlength: 10,
            step: 1,
            min: 0,
            type: "number",
          },
          confirmButtonText: "확인",
          cancelButtonText: "취소",
          showCancelButton: true,
        })
        .then((result) => {
          if (result.isConfirmed) {
            const quantity = parseInt(result.value, 10);
            if (!isNaN(quantity) && quantity > 0) {
              // 증가 함수 호출
              this.updateQuantity(quantity);
            }
          }
        });
    },

    // 수량 증가 함수
    async updateQuantity(newQuantity) {
      newQuantity = parseInt(newQuantity, 10);
      if (isNaN(newQuantity) || newQuantity < 0) {
        newQuantity = 0;
      }

      try {
        // 현재 수량에 증가할 수량을 더함
        const updatedQuantity = parseInt(this.localQuantity || 0) + newQuantity;

        const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/updateProductQuantities`, {
          productId: this.Item.num,
          quantity: updatedQuantity, // 증가된 새 수량
          companykey: this.$globalState.company_key
        });

        if (response.data.success) {
          // 성공 시 로컬 상태 업데이트
          this.localQuantity = updatedQuantity;

          // 합계 수량 업데이트
          this.updateAllQuantity();

          // 부모 컴포넌트에 업데이트 알림
          this.$emit("itemUpdated", {
            ...this.Item,
            quantity: updatedQuantity,
            all_quantity: this.localAllQuantity
          });

          console.log(`수량 업데이트 성공: ${this.localQuantity} → ${updatedQuantity}, 합계: ${this.localAllQuantity}`);
        } else {
          throw new Error(response.data.message || "수량 업데이트에 실패했습니다.");
        }
      } catch (error) {
        console.error("수량 업데이트 실패:", error);

        await this.$swal.fire({
          title: "오류",
          text: error.response?.data?.message || "수량 업데이트 중 오류가 발생했습니다.",
          icon: "error",
          confirmButtonText: "확인",
        });
      }
    },

    // 수량 감소 함수
    async decreaseQuantityupdate(newQuantity) {
      newQuantity = parseInt(newQuantity, 10);
      if (isNaN(newQuantity) || newQuantity < 0) {
        newQuantity = 0;
      }

      try {
        // 현재 수량에서 감소시킬 수량을 뺌 (음수가 되지 않도록 Math.max 사용)
        const currentQuantity = parseInt(this.localQuantity || 0);
        const updatedQuantity = Math.max(0, currentQuantity - newQuantity);

        // 감소할 수량이 현재 수량보다 많은 경우 경고
        if (newQuantity > currentQuantity) {
          await this.$swal.fire({
            title: "경고",
            text: `감소할 수량(${newQuantity})이 현재 수량(${currentQuantity})보다 많습니다. 수량이 0으로 설정됩니다.`,
            icon: "warning",
            confirmButtonText: "확인",
          });
        }

        const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/updateProductQuantities`, {
          productId: this.Item.num,
          quantity: updatedQuantity, // 감소된 새 수량
          companykey: this.$globalState.company_key
        });

        if (response.data.success) {
          // 성공 시 로컬 상태 업데이트
          this.localQuantity = updatedQuantity;

          // 합계 수량 업데이트
          this.updateAllQuantity();

          // 부모 컴포넌트에 업데이트 알림
          this.$emit("itemUpdated", {
            ...this.Item,
            quantity: updatedQuantity,
            all_quantity: this.localAllQuantity
          });

          console.log(`수량 업데이트 성공: ${currentQuantity} → ${updatedQuantity}, 합계: ${this.localAllQuantity}`);
        } else {
          throw new Error(response.data.message || "수량 업데이트에 실패했습니다.");
        }
      } catch (error) {
        console.error("수량 업데이트 실패:", error);

        await this.$swal.fire({
          title: "오류",
          text: error.response?.data?.message || "수량 업데이트 중 오류가 발생했습니다.",
          icon: "error",
          confirmButtonText: "확인",
        });
      }
    },

    // 낱개수량 관련 메서드 =======================================
    async decreaseQuantityPiece() {
      const currentQuantity = parseInt(this.localPieceQuantity || 0);

      if (currentQuantity > 0) {
        const newValue = currentQuantity - 1;
        console.log(`낱개수량 감소: ${currentQuantity} → ${newValue}`);
        await this.updateQuantityPiece(newValue, false); // false = 메시지 표시 안함
      }
    },

    async increaseQuantityPiece() {
      const currentQuantity = parseInt(this.localPieceQuantity || 0);

      const newValue = currentQuantity + 1;
      console.log(`낱개수량 증가: ${currentQuantity} → ${newValue}`);
      await this.updateQuantityPiece(newValue, false); // false = 메시지 표시 안함
    },

    // 낱개수량 업데이트 함수
    async updateQuantityPiece(value, showMessage = true) {
      // 입력값을 정수로 확실하게 변환
      const newValue = parseInt(value || 0);

      if (newValue < 0) {
        if (showMessage) {
          await this.$swal.fire({
            title: "입력 오류",
            text: "낱개수량은 0 이상이어야 합니다.",
            icon: "warning",
            confirmButtonText: "확인",
          });
        }
        return;
      }

      try {
        // 현재 값과 새 값을 로그로 출력
        console.log(`낱개수량 업데이트 요청: ${this.localPieceQuantity} → ${newValue}`);

        const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/updateProductQuantities`, {
          productId: this.Item.num,
          piece_quantity: newValue,
          companykey: this.$globalState.company_key
        });

        if (response.data.success) {
          // 성공 시 로컬 상태 업데이트
          this.localPieceQuantity = newValue;

          // 합계 수량 업데이트
          this.updateAllQuantity();

          console.log(`낱개수량 업데이트 성공: 현재 값 = ${this.localPieceQuantity}, 합계: ${this.localAllQuantity}`);

          // 부모 컴포넌트에 업데이트 알림
          this.$emit("itemUpdated", {
            ...this.Item,
            piece_quantity: newValue,
            all_quantity: this.localAllQuantity
          });
        } else {
          if (showMessage) {
            throw new Error(response.data.message || "낱개수량 수정에 실패했습니다.");
          }
        }
      } catch (error) {
        console.error("낱개수량 수정 실패:", error);
        if (showMessage) {
          await this.$swal.fire({
            title: "오류",
            text: error.response?.data?.message || "낱개수량 수정 중 오류가 발생했습니다.",
            icon: "error",
            confirmButtonText: "확인",
          });
        }
      }
    },

    async decreaseQuantityPieceInput() {
      const result = await this.$swal.fire({
        title: "일괄삭제 수량 입력",
        input: "number",
        inputAttributes: {
          autocapitalize: "off",
          required: true,
          minlength: 1,
          maxlength: 10,
          step: 1,
          min: 0,
          type: "number",
        },
        confirmButtonText: "확인",
        cancelButtonText: "취소",
        showCancelButton: true,
      });

      if (result.isConfirmed) {
        const quantity = parseInt(result.value, 10);
        if (!isNaN(quantity) && quantity > 0) {
          // 현재 수량에서 입력한 수량을 뺌 (음수가 되지 않도록 Math.max 사용)
          const currentQuantity = parseInt(this.localPieceQuantity || 0);
          const newValue = Math.max(0, currentQuantity - quantity);

          // 감소할 수량이 현재 수량보다 많은 경우 경고
          if (quantity > currentQuantity) {
            await this.$swal.fire({
              title: "경고",
              text: `감소할 수량(${quantity})이 현재 수량(${currentQuantity})보다 많습니다. 수량이 0으로 설정됩니다.`,
              icon: "warning",
              confirmButtonText: "확인",
            });
          }

          await this.updateQuantityPiece(newValue, true); // true = 메시지 표시
        }
      }
    },

    async increaseQuantityPieceInput() {
      const result = await this.$swal.fire({
        title: "일괄추가 수량 입력",
        input: "number",
        inputAttributes: {
          autocapitalize: "off",
          required: true,
          minlength: 1,
          maxlength: 10,
          step: 1,
          min: 0,
          type: "number",
        },
        confirmButtonText: "확인",
        cancelButtonText: "취소",
        showCancelButton: true,
      });

      if (result.isConfirmed) {
        const quantity = parseInt(result.value, 10);
        if (!isNaN(quantity) && quantity > 0) {
          // 현재 수량에 입력한 수량을 더함
          const currentQuantity = parseInt(this.localPieceQuantity || 0);
          const newValue = currentQuantity + quantity;

          await this.updateQuantityPiece(newValue, true); // true = 메시지 표시
        }
      }
    },

    // 안전재고수량 관련 메서드 =======================================
    async decreaseSafety() {
      if (this.localSafety > 0) {
        await this.updateSafety(this.localSafety - 1, false); // false = 메시지 표시 안함
      }
    },

    async increaseSafety() {
      await this.updateSafety(this.localSafety + 1, false); // false = 메시지 표시 안함
    },

    async updateSafety(value, showMessage = true) {
      const newValue = parseInt(value) || 0;
      if (newValue < 0) {
        if (showMessage) {
          await this.$swal.fire({
            title: "입력 오류",
            text: "안전재고수량은 0 이상이어야 합니다.",
            icon: "warning",
            confirmButtonText: "확인",
          });
        }
        return;
      }

      try {
        const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/updateProductQuantities`, {
          productId: this.Item.num,
          safety: newValue,
          companykey: this.$globalState.company_key
        });

        if (response.data.success) {
          console.log("안전재고 수량 업데이트:", this.localSafety);

          this.localSafety = newValue;

          // 메시지 표시가 필요한 경우에만 성공 메시지 표시
          if (showMessage) {
            await this.$swal.fire({
              title: "성공",
              text: "안전재고수량이 수정되었습니다.",
              icon: "success",
              confirmButtonText: "확인",
            });
          }

          // 부모 컴포넌트에 업데이트 알림
          this.$emit("itemUpdated", {
            ...this.Item,
            safety: newValue
          });
          return true;

        } else {
          if (showMessage) {
            throw new Error(response.data.message || "안전재고수량 수정에 실패했습니다.");
          }
        }
      } catch (error) {
        console.error("안전재고수량 수정 실패:", error);
        if (showMessage) {
          await this.$swal.fire({
            title: "오류",
            text: error.response?.data?.message || "안전재고수량 수정 중 오류가 발생했습니다.",
            icon: "error",
            confirmButtonText: "확인",
          });
        }
      }
    },

    async decreaseSafetyInput() {
      const result = await this.$swal.fire({
        title: "일괄삭제 수량 입력",
        input: "number",
        inputAttributes: {
          autocapitalize: "off",
          required: true,
          minlength: 1,
          maxlength: 10,
          step: 1,
          min: 0,
          type: "number",
        },
        confirmButtonText: "확인",
        cancelButtonText: "취소",
        showCancelButton: true,
      });

      if (result.isConfirmed) {
        const quantity = parseInt(result.value, 10);
        if (!isNaN(quantity) && quantity > 0) {
          await this.updateSafety(Math.max(0, this.localSafety - quantity), true); // true = 메시지 표시
        }
      }
    },

    async increaseSafetyInput() {
      const result = await this.$swal.fire({
        title: "일괄추가 수량 입력",
        input: "number",
        inputAttributes: {
          autocapitalize: "off",
          required: true,
          minlength: 1,
          maxlength: 10,
          step: 1,
          min: 0,
          type: "number",
        },
        confirmButtonText: "확인",
        cancelButtonText: "취소",
        showCancelButton: true,
      });

      if (result.isConfirmed) {
        const quantity = parseInt(result.value, 10);
        if (!isNaN(quantity) && quantity > 0) {
          await this.updateSafety(this.localSafety + quantity, true); // true = 메시지 표시
        }
      }
    },

    // === 제품 삭제 관련 메서드 ===
    deleteProduct() {
      this.$emit("deleteProduct", this.Item.id);
    },
    async confirmDelete() {
      try {
        // 삭제 확인 다이얼로그
        const result = await this.$swal.fire({
          title: "품목 삭제",
          text: "정말로 이 품목을 삭제하시겠습니까?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "삭제",
          cancelButtonText: "취소",
        });

        if (result.isConfirmed) {
          // 삭제 API 호출
          const response = await this.$axios.post(
            `${this.$apiUrls.hanqmedi}/deleteProducts`,
            {
              productId: this.Item.num,
              companykey: this.$globalState.company_key,
            }
          );

          if (response.data.statusCode === 200) {
            // 삭제 성공
            await this.$swal.fire({
              title: "삭제 완료",
              text: "품목이 성공적으로 삭제되었습니다.",
              icon: "success",
              confirmButtonText: "확인",
            });

            // 먼저 부모 컴포넌트에 삭제 이벤트 전달
            this.$emit("productDeleted", this.Item.num);
            this.$emit("closeModal");
          } else {
            throw new Error(response.data.message || "품목 삭제에 실패했습니다.");
          }
        }
      } catch (error) {
        console.error("품목 삭제 중 오류 발생:", error);
        await this.$swal.fire({
          title: "오류 발생",
          text: error.response?.data?.message || "품목 삭제 중 오류가 발생했습니다.",
          icon: "error",
          confirmButtonText: "확인",
        });
      }
    },

    // === QR 코드 관련 메서드 ===
    async generateQRCode() {
      if (this.Item && this.Item.options.bcode[0]) {
        try {
          this.qrCodeUrl = await QRCode.toDataURL(this.Item.options.bcode[0]);
        } catch (error) {
          console.error("QR 코드 생성 오류:", error);
        }
      }
    },
    downloadQRCode() {
      if (!this.qrCodeUrl) {
        this.$swal.fire({
          title: "알림",
          text: "QR 코드가 생성되지 않았습니다.",
          icon: "warning",
          confirmButtonText: "확인",
        });
        return;
      }
      const link = document.createElement("a");
      link.href = this.qrCodeUrl;
      link.download = `qr-code-${this.Item.bcode}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // === 가격 수정 관련 메서드 ===
    toggleEditMode() {
      this.isEditing = !this.isEditing;
      if (this.isEditing) {
        this.editedPrice =
          this.localPrice === "0" || this.localPrice === 0
            ? ""
            : this.localPrice.toString();
      }
    },
    onPriceInput(event) {
      const value = event.target.value;
      if (value === "") {
        this.editedPrice = "";
      } else {
        this.editedPrice = value.replace(/[^0-9]/g, "");
      }
    },
    // 대여가격 수정 모드 토글
    toggleRentPriceEditMode() {
      this.isEditingRentPrice = !this.isEditingRentPrice;
      if (this.isEditingRentPrice) {
        this.editedRentPrice =
          this.localRentPrice === "0" || this.localRentPrice === 0
            ? ""
            : this.localRentPrice.toString();
      }
    },
    // 대여가격 입력 처리
    onRentPriceInput(event) {
      const value = event.target.value;
      if (value === "") {
        this.editedRentPrice = "";
      } else {
        this.editedRentPrice = value.replace(/[^0-9]/g, "");
      }
    },
    // 공급가격 수정 모드 토글
    toggleSupplyPriceEditMode() {
      this.isEditingSupplyPrice = !this.isEditingSupplyPrice;
      if (this.isEditingSupplyPrice) {
        this.editedSupplyPrice =
          this.localSupplyPrice === "0" || this.localSupplyPrice === 0
            ? ""
            : this.localSupplyPrice.toString();
      }
    },
    // 공급가격 입력 처리
    onSupplyPriceInput(event) {
      const value = event.target.value;
      if (value === "") {
        this.editedSupplyPrice = "";
      } else {
        this.editedSupplyPrice = value.replace(/[^0-9]/g, "");
      }
    },
    async updateProduct() {
      // 판매가격, 대여가격, 공급가격 중 하나라도 수정 중이 아니면 리턴
      if (!this.isEditing && !this.isEditingRentPrice && !this.isEditingSupplyPrice)
        return;

      try {
        // 콤마가 포함된 문자열을 숫자로 안전하게 변환하는 함수
        const parsePrice = (price) => {
          if (price === undefined || price === null) return 0;

          // 문자열인 경우 콤마 제거
          if (typeof price === "string") {
            price = price.replace(/,/g, "");
          }

          const num = Number(price);
          return isNaN(num) ? 0 : num;
        };

        // 기본 데이터 준비 - 안전하게 숫자로 변환
        const updateData = {
          b_product_id: this.Item.num,
          buyprice: parsePrice(this.localPrice), // 콤마 제거 후 변환
          rentprice: parsePrice(this.localRentPrice),
          supplyprice: parsePrice(this.localSupplyPrice),
        };

        // 판매가격 수정 중인 경우만 업데이트
        if (this.isEditing) {
          const newPrice = parsePrice(this.editedPrice);
          if (newPrice < 0) {
            await this.$swal.fire({
              title: "입력 오류",
              text: "올바른 판매가격을 입력해주세요.",
              icon: "warning",
              confirmButtonText: "확인",
            });
            return;
          }
          updateData.buyprice = newPrice;
        }

        // 대여가격 수정 중인 경우만 업데이트
        if (this.isEditingRentPrice) {
          const newRentPrice = parsePrice(this.editedRentPrice);
          if (newRentPrice < 0) {
            await this.$swal.fire({
              title: "입력 오류",
              text: "올바른 대여가격을 입력해주세요.",
              icon: "warning",
              confirmButtonText: "확인",
            });
            return;
          }
          updateData.rentprice = newRentPrice;
        }

        // 공급가격 수정 중인 경우만 업데이트
        if (this.isEditingSupplyPrice) {
          const newSupplyPrice = parsePrice(this.editedSupplyPrice);
          if (newSupplyPrice < 0) {
            await this.$swal.fire({
              title: "입력 오류",
              text: "올바른 공급가격을 입력해주세요.",
              icon: "warning",
              confirmButtonText: "확인",
            });
            return;
          }
          updateData.supplyprice = newSupplyPrice;
        }

        // 디버깅용 로그
        console.log("가격 업데이트 요청 데이터:", updateData);

        const response = await this.$axios.post(
          `${this.$apiUrls.hanqmedi}/productPriceUpdate`,
          updateData
        );

        // 서버 응답 처리
        if (response.data.message === "price update success") {
          const updatedItem = { ...this.Item };

          // 판매가격 수정 중이었다면 판매가격 업데이트
          if (this.isEditing) {
            const newPrice = parsePrice(this.editedPrice);
            this.localPrice = newPrice;
            updatedItem.price = newPrice;
            this.isEditing = false;
          }

          // 대여가격 수정 중이었다면 대여가격 업데이트
          if (this.isEditingRentPrice) {
            const newRentPrice = parsePrice(this.editedRentPrice);
            this.localRentPrice = newRentPrice;
            updatedItem.price2 = newRentPrice;
            this.isEditingRentPrice = false;
          }

          // 공급가격 수정 중이었다면 공급가격 업데이트
          if (this.isEditingSupplyPrice) {
            const newSupplyPrice = parsePrice(this.editedSupplyPrice);
            this.localSupplyPrice = newSupplyPrice;
            updatedItem.price3 = newSupplyPrice;
            this.isEditingSupplyPrice = false;
          }

          // 부모 컴포넌트에 업데이트 이벤트 발생
          this.$emit("itemUpdated", updatedItem);

          await this.$swal.fire({
            title: "성공",
            text: "가격이 성공적으로 업데이트되었습니다.",
            icon: "success",
            confirmButtonText: "확인",
          });
        } else {
          throw new Error(response.data.message || "가격 업데이트에 실패했습니다.");
        }
      } catch (error) {
        console.error("가격 업데이트 중 오류 발생:", error);
        console.error("오류 세부 정보:", error.response?.data || error);
        await this.$swal.fire({
          title: "오류",
          text:
            error.response?.data?.message ||
            error.message ||
            "가격 업데이트 중 오류가 발생했습니다.",
          icon: "error",
          confirmButtonText: "확인",
        });
      }
    },

    formatPrice(price) {
      return price ? price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0";
    },

    // ===========================================================
    // 바코드 스캐너 관련 메서드
    // ===========================================================
    toggleBarcodeScanner() {
      if (this.isScannerActive) {
        // 스캐너가 활성화 상태면 비활성화
        this.disableBarcodeScanner();
      } else {
        // 스캐너가 비활성화 상태면 활성화
        // 이미 스캐너가 연결되어 있는지 체크
        if (onScan.isAttachedTo(document)) {
          onScan.detachFrom(document);
        }

        // 스캐너 연결
        this.initBarcodeScanner();
        this.isScannerActive = true;
      }
    },

    // 바코드 스캐너
    initBarcodeScanner() {
      // 이미 연결된 경우 먼저 분리
      if (onScan.isAttachedTo(document)) {
        onScan.detachFrom(document);
      }

      // 새로 연결
      onScan.attachTo(document, {
        suffixKeyCodes: [13], // Enter 키로 바코드 스캔 완료
        reactToPaste: false,
        onScan: this.handleScan,
        onScanError: (error) => {
          console.error("Scan error:", error);
        },
        onKeyDetect: (keyCode, event) => {
          // 텍스트 선택 관련 키는 허용 (Ctrl+A, Ctrl+C 등)
          if (event.ctrlKey || event.metaKey) {
            return; // 복사, 붙여넣기, 전체선택 등 허용
          }

          // 텍스트 선택을 위한 키들 허용
          const allowedKeys = [16, 17, 18, 37, 38, 39, 40, 91, 93]; // Shift, Ctrl, Alt, 화살표키, Cmd
          if (allowedKeys.includes(keyCode)) {
            return;
          }

          // 입력 필드에 포커스가 있는 경우 바코드 스캔 무시
          const activeElement = document.activeElement;
          if (
            activeElement &&
            (activeElement.tagName === "INPUT" ||
              activeElement.tagName === "TEXTAREA" ||
              activeElement.tagName === "SELECT" ||
              activeElement.contentEditable === "true")
          ) {
            console.log("입력 필드에 포커스가 있어서 바코드 스캔 무시");
            return;
          }
        },
        timeBeforeScanTest: 100, // 바코드 스캔 감지 시간을 더 짧게 (100ms)
        avgTimeByChar: 50, // 문자당 평균 시간을 더 짧게 (50ms)
        minLength: 8, // 최소 8자리 이상
        scanButtonKeyCode: false,
        scanButtonLongPressThreshold: 500,
        stopPropagation: false,
        preventDefault: false,
        onReceive: (sCode, qty) => {
          if (qty > 5) {
            console.log("바코드 스캐너로 인식됨");
          } else {
            console.log("키보드 입력으로 인식됨 - 무시");
            return false; // 스캔 처리하지 않음
          }
        },
      });
    },

    async handleScan(sCode) {
      try {
        // 품목 선택 확인
        if (!this.Item || !this.Item.num) {
          this.disableBarcodeScanner();
          await this.$swal.fire({
            title: "오류",
            text: "선택된 품목이 없습니다. 품목을 먼저 선택해주세요.",
            icon: "warning",
            confirmButtonText: "확인",
          });
          return;
        }

        // 현재 품목이 이미 바코드를 가지고 있는지 확인
        const currentBcode = this.Item.bcode || this.Item.options?.bcode?.[0];

        // 동일한 바코드를 다시 스캔하는 경우
        if (currentBcode === sCode) {
          await this.$swal.fire({
            title: "알림",
            text: "이미 등록된 바코드입니다.",
            icon: "warning",
            confirmButtonText: "확인",
          });
          return;
        }

        // 현재 품목이 이미 다른 바코드를 가지고 있는 경우
        if (currentBcode && currentBcode !== sCode) {
          const confirmResult = await this.$swal.fire({
            title: "바코드 변경",
            text: `현재 등록된 바코드(${currentBcode})를 새 바코드(${sCode})로 변경하시겠습니까?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "변경",
            cancelButtonText: "취소",
          });

          if (!confirmResult.isConfirmed) {
            return;
          }
        }

        this.scannedBarcode = sCode;

        try {
          let shouldProceed = true;
          let existingProduct = null;

          // 먼저 동일한 바코드가 다른 제품에 등록되어 있는지 확인
          try {
            const checkResponse = await this.$axios.post(
              `${this.$apiUrls.hanqmedi}/searchProductByBarcode`,
              {
                barcode: sCode,
                companykey: this.$globalState.company_key,
              }
            );

            // 기존 제품이 발견된 경우
            if (checkResponse.data.success && checkResponse.data.product) {
              existingProduct = checkResponse.data.product;

              // 현재 제품과 다른 제품인 경우에만 경고
              if (existingProduct.num !== this.Item.num) {
                const confirmResult = await this.$swal.fire({
                  title: "중복 바코드 확인",
                  html: `이 바코드는 이미 다른 제품에도 등록되어 있습니다.<br><br><strong>기존 제품:</strong><br>품목명: ${existingProduct.name3}<br><br>그래도 등록하시겠습니까?<br><small style="color: #666;">(동일한 바코드가 여러 제품에 등록됩니다)</small>`,
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonText: "등록",
                  cancelButtonText: "취소",
                  confirmButtonColor: "#0f4c5c",
                });

                if (!confirmResult.isConfirmed) {
                  shouldProceed = false;
                  this.scannedBarcode = "";
                  this.disableBarcodeScanner();
                  return;
                }
              }
            }
          } catch (checkError) {
            if (checkError.response && checkError.response.status === 404) {
              console.log("새로운 바코드 등록:", sCode);
            } else {
              console.error("바코드 검색 중 오류:", checkError);
            }
          }

          if (shouldProceed) {
            const response = await this.$axios.post(
              `${this.$apiUrls.hanqmedi}/productCodeInsert`,
              {
                bcode: sCode,
                b_item_id: this.Item.num,
                companykey: this.$globalState.company_key,
                force: currentBcode ? true : false // 기존 바코드가 있으면 강제 등록
              }
            );

            if (response.data.statusCode === 200 && response.data.success) {
              this.disableBarcodeScanner();

              let successMessage = response.data.message;

              // 중복 제품 정보가 있는 경우 추가 정보 표시
              if (response.data.data?.duplicateProducts && response.data.data.duplicateProducts.length > 0) {
                const duplicateList = response.data.data.duplicateProducts
                  .map(product => `• ${product.name3}`)
                  .join('<br>');

                successMessage += `<br><br><small style="color: #666;"><strong>동일한 바코드를 가진 다른 품목들:</strong><br>${duplicateList}</small>`;
              }

              await this.$swal.fire({
                title: "등록 완료",
                html: successMessage,
                icon: "success",
                confirmButtonText: "확인",
              });

              this.$emit("itemUpdated", {
                ...this.Item,
                bcode: sCode,
                options: {
                  ...this.Item.options,
                  bcode: [sCode],
                },
              });
            } else {
              throw new Error(response.data.message || "바코드 등록에 실패했습니다.");
            }
          }
        } catch (error) {
          this.scannedBarcode = "";

          let errorMessage = "바코드 등록 중 오류가 발생했습니다.";

          if (error.response && error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }

          this.disableBarcodeScanner();

          await this.$swal.fire({
            title: "바코드 등록 실패",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "확인",
          });
        }
      } catch (error) {
        // 전체 스캔 프로세스 중 발생할 수 있는 예외 처리
        this.scannedBarcode = "";
        this.disableBarcodeScanner();

        await this.$swal.fire({
          title: "예상치 못한 오류",
          text: `바코드 스캔 중 예상치 못한 오류가 발생했습니다. 개발자에게 문의하세요: ${error.message || "알 수 없는 오류"}`,
          icon: "error",
          confirmButtonText: "확인",
        });
      } finally {
        this.isScannerActive = false;
      }
    },

    disableBarcodeScanner() {
      if (onScan.isAttachedTo(document)) {
        onScan.detachFrom(document);
        this.isScannerActive = false;
      }
    },

    enableBarcodeScanner() {
      // 편집 모드가 아닐 때만 스캐너 재활성화
      if (!this.isEditMode && !this.isScannerActive && !onScan.isAttachedTo(document)) {
        this.isScannerActive = true;
        this.initBarcodeScanner();
      }
    },

    async deleteBarcode() {
      try {
        // 현재 품목의 바코드 확인
        const currentBcode = this.Item.options?.bcode?.[0];
        const itemId = this.Item?.num;

        console.log("삭제 시도:", { currentBcode, itemId });

        if (!currentBcode || !this.Item.num) {
          await this.$swal.fire({
            title: "알림",
            text: "삭제할 바코드가 없습니다.",
            icon: "warning",
            confirmButtonText: "확인",
          });
          return;
        }

        // 삭제 확인 다이얼로그
        const result = await this.$swal.fire({
          title: "바코드 삭제",
          text: "바코드를 삭제하시겠습니까?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "삭제",
          cancelButtonText: "취소",
        });

        if (!result.isConfirmed) {
          return;
        }

        const response = await this.$axios.post(
          `${this.$apiUrls.hanqmedi}/productCodeDelete`,
          {
            bcode: currentBcode,
            b_item_id: itemId,
            companykey: this.$globalState.company_key,
          }
        );
        console.log("삭제 응답:", response.data);

        if (response.data.statusCode === 200) {
          await this.$swal.fire({
            title: "성공",
            text: "바코드가 성공적으로 삭제되었습니다.",
            icon: "success",
            confirmButtonText: "확인",
          });

          // 로컬 상태 업데이트
          this.scannedBarcode = "";

          // 부모 컴포넌트에 업데이트 이벤트 발생
          this.$emit("itemUpdated", {
            ...this.Item,
            bcode: "",
            options: {
              ...this.Item.options,
              bcode: [],
            },
          });

          // 상세 정보 새로고침
          this.$emit("closeModal");
          await this.getProductsDetail();
        } else {
          await this.$swal.fire({
            icon: "error",
            title: "오류",
            text: response.data.message || "바코드 삭제에 실패했습니다.",
            confirmButtonText: "확인",
          });
        }
      } catch (error) {
        console.error("바코드 삭제 중 오류:", error);
        await this.$swal.fire({
          icon: "error",
          title: "오류",
          text: error.message || "바코드 삭제 중 오류가 발생했습니다.",
          confirmButtonText: "확인",
        });
      }
    },

    // ESC 키 처리
    handleEscKey(event) {
      if (event.key === "Escape") {
        // 이미지 검색 모달이 열려있으면 ESC 키 무시
        if (this.showImageSearchModal) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.$emit("closeModal");
      }
    },

    showDevelopmentMessage() {
      this.$swal.fire({
        title: '개발 진행중',
        text: '현재 개발 중인 기능입니다.',
        icon: 'info',
        confirmButtonColor: '#3085d6',
        confirmButtonText: '확인'
      });
    },
  },

  mounted() {
    this.getProductsDetail();
    this.generateQRCode();
    this.getColorTable();
    this.updateAllQuantity();
    this.fetchAllCategories();

    // 초기값 설정
    this.localPieceQuantity = this.Item.piece_quantity || 0;
    this.localSafety = this.Item.safety || 0;

    this.initializeEditedItem();

    // 바코드 스캐너 자동 활성화
    this.isScannerActive = true;
    this.initBarcodeScanner();

    document.addEventListener('keydown', this.handleEscKey);
  },

  beforeUnmount() {
    // 컴포넌트가 제거되기 전에 스캐너 분리
    this.disableBarcodeScanner();

    document.removeEventListener('keydown', this.handleEscKey);

    // 프리뷰 URL 정리
    if (this.previewImageUrl) {
      URL.revokeObjectURL(this.previewImageUrl);
    }
  },

  watch: {
    // Item prop이 변경될 때 editedItem 업데이트
    Item: {
      handler(newItem) {
        if (newItem) {
          this.initializeEditedItem();
        }
      },
      immediate: true
    },
    "Item.price": function (newPrice) {
      this.localPrice = newPrice;
    },
    "Item.price2": function (newRentPrice) {
      this.localRentPrice = newRentPrice;
    },
    "Item.price3": function (newSupplyPrice) {
      this.localSupplyPrice = newSupplyPrice;
    },
    "Item.piece_quantity": function (newQuantityPiece) {
      this.localPieceQuantity = newQuantityPiece || 0;
    },
    "Item.safety": function (newSafety) {
      this.localSafety = newSafety || 0;
    },
    // 카테고리1 변경 감지
    "editedItem.name0": function (newValue) {
      if (newValue) {
        this.onCategory1Change();
      }
    },
  },
};
</script>

<style>
.custom-toast-container {
  top: 50px !important;
}
</style>
